import type { ReactNode } from 'react';

import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { AirlineLogo } from '../../shared/airline_logo/airline_logo.js';
import type { CityOfAirport } from './filter_logic.js';
import { fmtWeekdayDayMonth, fmtMoneyWhole, fmtTime } from './flight_format.js';
import type { FlightMoneySource, FlightSegmentSource } from './flight_offer_source.js';
import { sumMoney } from './sum_money.js';
import './leg_stepper.css';

/** Os textos do stepper do roteiro, por prop (#4997). O genui-kit monta este
 *  objeto em `legStepperCopy(locale)`; texto com valor é função. */
export type LegStepperCopy = {
  readonly title: string;
  readonly chosenCount: (chosen: number, total: number) => string;
  readonly total: string;
  readonly partialTotal: string;
  readonly choosingNow: string;
  readonly waiting: string;
  readonly waitingOutbound: string;
  readonly stepDone: (n: number) => string;
  readonly removePick: (n: number) => string;
  readonly leg: (n: number) => string;
  readonly bestOptions: (count: number) => string;
};

/** O que o stepper lê do host além dos passos: os textos, o idioma das datas e
 *  o cadastro de aeroportos (a praça `GRU · São Paulo`). */
export type LegStepperText = {
  readonly copy: LegStepperCopy;
  readonly locale: string;
  readonly cityOf: CityOfAirport;
};

/**
 * Um passo do stepper.
 *
 * **Quem diz "resolvido" é `chosenFlight` OU `chosenPrice`, e a distinção não é
 * zelo.** Na IDA do round-trip a escolha existe SEM preço — o par só precifica no
 * segundo passo, e o `fareId` final sai do ref de volta —, então amarrar o
 * concluído ao preço deixava a ida escolhida contando como pendente: `0 de 2
 * escolhidos` com o voo da ida já na tela, e o Confirmar sem explicação pra estar
 * travado. `chosenPrice` segue sendo o ÚNICO insumo do parcial (o stepper não soma
 * nada por conta própria).
 */
export interface LegStep {
  origin: string;
  destination: string;
  departDate: string;
  chosenPrice?: FlightMoneySource | undefined;
  /** O voo que fechou o passo — vira o chip de detalhe dentro do card. */
  chosenFlight?: FlightSegmentSource | undefined;
}

/** O passo está resolvido? Ver a nota do `LegStep`. Exportado: é o MESMO
 *  predicado que o placar da coluna do explorer usa (`scoreOf`,
 *  `explorer_results.tsx`) — nunca duplicar. */
export function isResolved(step: LegStep): boolean {
  return Boolean(step.chosenPrice) || Boolean(step.chosenFlight);
}

/**
 * O chip do voo escolhido, DENTRO do card do passo: marca da cia, o código do voo
 * e os dois horários com as praças. É o que responde "o que eu escolhi aqui?" sem
 * reabrir o trecho — antes o card dizia só o preço, e o preço não identifica voo.
 *
 * O ✕ é o gesto de DESFAZER daquele passo, e mora no chip porque é o que ele
 * desfaz. Sem `onRemove` (card congelado/histórico) o chip fica sem ✕: lá a
 * escolha já foi despachada e não há o que remover.
 */
function PickChip({
  n,
  flight,
  copy,
  onRemove,
}: {
  readonly n: number;
  readonly flight: FlightSegmentSource;
  readonly copy: LegStepperCopy;
  readonly onRemove?: () => void;
}) {
  return (
    <span className="fls-pick">
      <AirlineLogo iata={flight.carrier} name={flight.carrierName} size={16} />
      <b className="fls-pick-code">
        {flight.carrier} {flight.flightNumber}
      </b>
      {/* `—` e não `→` entre os horários: a seta é a gramática da ROTA (a faixa
          acima, `GRU · São Paulo → REC · Recife`), e repeti-la aqui fazia o chip
          ler como um segundo trecho em vez do intervalo de UM voo. */}
      <span className="fls-pick-when">
        · {fmtTime(flight.departAt)} {flight.from} — {fmtTime(flight.arriveAt)} {flight.to}
      </span>
      {onRemove && (
        <button
          type="button"
          className="fls-pick-x"
          aria-label={copy.removePick(n)}
          onClick={onRemove}
        >
          <XIcon size={11} aria-hidden />
        </button>
      )}
    </span>
  );
}

/**
 * A bolha do passo: ela carrega a ORDEM — é o que autoriza o passo futuro a existir
 * apagado. Concluído troca o número pelo tique: o número já foi cumprido, e o que
 * importa dele agora é que acabou. O tique é `aria-hidden`, então no concluído a
 * bolha vira `role="img"` e reempossa por rótulo o que o número dizia — senão o
 * passo pronto era anunciado sem ordem e sem estado, e "concluído" ficava só na cor.
 *
 * **Aqui o concluído vence o aberto** — e por código, não pela ordem do sheet (que
 * um refactor de CSS reordena sem perceber): quem diz "onde eu estou" num trecho já
 * resolvido é o anel do card, e o verde é o único sinal de "escolhido" que o passo
 * tem.
 */
function StepBubble({
  n,
  done,
  now,
  copy,
}: { readonly n: number; readonly copy: LegStepperCopy } & StepState) {
  return (
    <span
      className={`fls-bubble${now && !done ? ' is-now' : ''}${done ? ' is-done' : ''}`}
      role={done ? 'img' : undefined}
      aria-label={done ? copy.stepDone(n) : undefined}
    >
      {done ? <CheckIcon size={14} aria-hidden /> : n}
    </span>
  );
}

/** A praça de um passo: `GRU · São Paulo`, ou só o código quando o IATA está
 *  fora do cadastro do domain. Resolvida AQUI e não em `LegStep` porque é
 *  apresentação — engrossar o tipo obrigaria os dois hosts (explorer e card
 *  multi) a threadar cidade que nenhum dos dois tem em mãos. */
function placeLabel(iata: string, cityOf: CityOfAirport): string {
  const city = cityOf(iata);
  return city ? `${iata} · ${city}` : iata;
}

/**
 * É stepper, não aba, e a distinção muda o desenho: há número, ordem, status por
 * passo, contador e total parcial. Aba é entre pares sem ordem e sem estado —
 * seria ela que autorizaria três passos idênticos sem começo nem fim.
 */
function Step({
  n,
  step,
  state,
  showDate,
  waitingFor,
  text,
  onGoTo,
  onRemove,
}: {
  readonly n: number;
  readonly step: LegStep;
  readonly text: LegStepperText;
  readonly state: StepState;
  /** A data mora no passo quando o host NÃO tem título de página que já a diga.
   *  O explorer tem (é ele que passa `headless`); o card multi do chat não. */
  readonly showDate: boolean;
  /** Quem a espera aguarda, quando o host sabe nomear. Ausente ⇒ genérico: num
   *  roteiro de N trechos não existe "a ida". */
  readonly waitingFor?: 'outbound';
  readonly onGoTo: () => void;
  readonly onRemove?: () => void;
}) {
  const { copy, locale, cityOf } = text;
  const { done, now } = state;
  // O preço vence "Escolhendo agora…" quando o trecho JÁ está resolvido, mesmo
  // com ele aberto: senão o passo dizia "escolhendo" enquanto o placar ao lado
  // dizia "3 de 3 escolhidos" — duas afirmações contrárias a 40px uma da outra.
  // Resolvido SEM preço (a ida do round-trip) não tem número pra mostrar, e o chip
  // logo abaixo já diz o que ficou: aí o status se cala em vez de mentir
  // "escolhendo agora".
  let status: string | null;
  if (step.chosenPrice) {
    status = fmtMoneyWhole(step.chosenPrice);
  } else if (step.chosenFlight) {
    status = null;
  } else if (now) {
    status = copy.choosingNow;
  } else {
    status = waitingFor === 'outbound' ? copy.waitingOutbound : copy.waiting;
  }

  // O card deixou de ser UM botão: o ✕ do chip é um botão próprio, e botão dentro
  // de botão é DOM inválido. Quem navega agora é a faixa de cima (`.fls-step-go`),
  // e a moldura/hover ficaram na casca.
  return (
    <div className={`fls-step${now ? ' is-now' : ''}${done ? ' is-done' : ''}`}>
      <button
        type="button"
        onClick={onGoTo}
        aria-current={now ? 'step' : undefined}
        className="fls-step-go"
      >
        <StepBubble n={n} done={done} now={now} copy={copy} />
        <span className="fls-body">
          <span className="fls-route">
            <b>
              {placeLabel(step.origin, cityOf)} → {placeLabel(step.destination, cityOf)}
            </b>
            {showDate && (
              <span className="fls-date">· {fmtWeekdayDayMonth(step.departDate, locale)}</span>
            )}
          </span>
          {status !== null && <span className="fls-status">{status}</span>}
        </span>
      </button>
      {step.chosenFlight && (
        <PickChip
          n={n}
          flight={step.chosenFlight}
          copy={copy}
          {...(onRemove ? { onRemove } : {})}
        />
      )}
    </div>
  );
}

/**
 * O estado de um passo — **dois eixos, não três valores**: `done` é a escolha
 * daquele trecho, `now` é onde a pessoa está. Eles se cruzam no caso que importa:
 * o trecho aberto E resolvido (o ÚLTIMO pick de um roteiro, e a volta do
 * round-trip, ficam abertos porque não há pendente pra onde avançar). Com um enum
 * de três valores o ativo engolia o concluído ali, e o passo mostrava o NÚMERO com
 * o placar ao lado dizendo `2 de 2 escolhidos` — o tique verde é o sinal de
 * "escolhido" da tela inteira, e escondê-lo justo no trecho que acabou de fechar
 * era a leitura de pendente.
 *
 * `frozen` derruba o `now`: congelado ninguém está escolhendo, então o "onde eu
 * estou" não tem referente (é o que sobra no histórico da conversa).
 */
interface StepState {
  done: boolean;
  now: boolean;
}

function stepState(index: number, active: number, step: LegStep, frozen: boolean): StepState {
  return { done: isResolved(step), now: index === active && !frozen };
}

/**
 * O cabeçalho do roteiro: título, placar, a fila de passos e a linha do trecho
 * aberto (as ações do roteiro moram na SAIA do card — M3). **Sem estado próprio** — recebe os passos e o
 * índice ativo e devolve cliques. O placar é DERIVADO dos passos, então não existe
 * caminho em que o contador e a soma discordem do que está escolhido.
 */
export function LegStepper({
  steps,
  active,
  onGoTo,
  onRemove,
  openOptionsCount,
  frozen,
  collapsed,
  headless,
  waitingFor,
  text,
  children,
}: {
  readonly steps: readonly LegStep[];
  /** Índice do trecho aberto. */
  readonly active: number;
  readonly onGoTo: (i: number) => void;
  /** Desfazer a escolha DE UM passo (o ✕ do chip). Ausente ⇒ chip sem ✕: no card
   *  congelado a escolha já foi despachada e não há o que remover. */
  readonly onRemove?: (i: number) => void;
  /** Card em leitura: nenhum passo é "onde eu estou" — o anel e o
   *  `aria-current` saem da fila inteira (ver `stepState`). */
  readonly frozen?: boolean;
  /** Roteiro completo dobrado (UX Pacote 2, M3): a linha do trecho aberto e o
   *  bloco de opções saem de cena — o que sobra é a fila de passos (com os chips)
   *  e a saia do card, onde mora o único caminho de avanço. Quem decide é o card
   *  (completo && sem revisão aberta); reabrir é clicar um passo. */
  readonly collapsed?: boolean;
  /** Esconde o `.fls-top` (título + placar `N de M` + total): o HOST já
   *  desenha o próprio placar (a coluna do explorer, `.fex-headline`/`scoreOf`
   *  — barra única, Task 5). A fila de passos e a linha do trecho aberto
   *  seguem iguais. O card do chat (`flight_multi_offers`) NÃO passa: lá o
   *  placar é deste componente. */
  readonly headless?: boolean;
  /** Quem a espera dos passos pendentes aguarda. Só o round trip tem resposta
   *  ("a ida"); num roteiro de N trechos não existe, e aí a cópia fica genérica.
   *  Quem sabe a forma da viagem é o host, então é ele que nomeia. */
  readonly waitingFor?: 'outbound';
  /** Quantas opções o trecho aberto traz — a linha "Trecho N · 3 melhores opções". */
  readonly openOptionsCount?: number;
  readonly text: LegStepperText;
  /** O trio do trecho aberto. Entra por dentro do bloco porque o bloco é deste
   *  componente — e ele não tem moldura (ver o CSS). */
  readonly children?: ReactNode;
}) {
  const { copy } = text;
  const chosen = steps.filter(isResolved).length;
  const complete = steps.length > 0 && chosen === steps.length;
  const partial = sumMoney(steps.flatMap((s) => (s.chosenPrice ? [s.chosenPrice] : [])));
  const open = steps[active];

  return (
    <div className="fls">
      {!headless && (
        <div className="fls-top">
          <h4 className="fls-ttl">{copy.title}</h4>
          <span className="fls-score">
            <span className="fls-count">{copy.chosenCount(chosen, steps.length)}</span>
            {/* "Total parcial" ENQUANTO falta trecho: ali o número não é o que a viagem
                custa, é o que já foi decidido dela — `Total` num roteiro 2 de 3 é o tipo
                de número que a pessoa leva pra aprovação e descobre errado depois. No
                último trecho ele para de ser parcial, e o rótulo troca: é o sinal de
                "acabou" mais honesto que existe aqui, porque não precisa de palavra nova
                — a mesma linha passa a afirmar outra coisa, junto do CTA destravando no
                rodapé. Sem nenhum trecho resolvido não há parcial a mostrar, e o
                `sumMoney` também se cala em moedas diferentes. */}
            {partial && (
              <span className="fls-partial">
                {complete ? copy.total : copy.partialTotal} · <b>{fmtMoneyWhole(partial)}</b>
              </span>
            )}
          </span>
        </div>
      )}

      <div className="fls-steps">
        {steps.map((s, i) => (
          <Step
            // Índice na chave: rota+data NÃO é única por contrato. Um roteiro
            // pode repetir o mesmo trecho na mesma data (ida e volta no dia, dois
            // voos para remarcar), e aí duas chaves iguais fazem o React
            // reaproveitar o passo errado.
            key={`${String(i)}-${s.origin}-${s.destination}-${s.departDate}`}
            n={i + 1}
            step={s}
            state={stepState(i, active, s, frozen ?? false)}
            // `headless` é o host que tem a própria moldura — e, com ela, o título
            // de página que já diz a data. Reusar essa prop em vez de criar outra
            // mantém UM seam para "o host já disse isso".
            showDate={!headless}
            {...(waitingFor ? { waitingFor } : {})}
            text={text}
            onGoTo={() => {
              onGoTo(i);
            }}
            {...(onRemove && !frozen
              ? {
                  onRemove: () => {
                    onRemove(i);
                  },
                }
              : {})}
          />
        ))}
      </div>

      {/* A linha do trecho aberto só nomeia a rodada — o placar mora lá em cima, e
          a data saiu daqui porque o passo ativo, 8px acima, já a escreve. */}
      {/* Sem `openOptionsCount` a linha inteira sai (#2994): no explorer a contagem
          mora na meta da página e o título já nomeia o trecho aberto. */}
      {!collapsed && open && openOptionsCount != null && (
        <div className="fls-open">
          <span className="fls-open-ttl">{copy.leg(active + 1)}</span>
          <span className="fls-open-count">{copy.bestOptions(openOptionsCount)}</span>
        </div>
      )}

      {!collapsed && children && <div className="fls-trio">{children}</div>}
    </div>
  );
}
